import { useEffect, useRef, useSyncExternalStore } from "react";
import { useCartStore } from "@/stores/cartStore";
import { useAuth } from "@/contexts/AuthContext";
import { syncCartSnapshot } from "@/lib/cartSnapshot.functions";
import { syncAnonCartSnapshot } from "@/lib/cartSnapshotAnon.functions";
import { getVisitorId } from "@/lib/activityTracker";
import { getAuthSnapshot } from "@/lib/auth-snapshot";
import { onRecoveryMutation, CART_RECOVERY_SOURCE_ENABLED, recoveryEligibilityState } from "@/lib/cartRecovery";
import { createRecoveryReceiptQueue } from "@/lib/cartRecoveryReceipts";
import { recordCartRecoveryMutation, quarantineCartRecovery, getCartRecoveryEligibility } from "@/lib/cartRecovery.functions";

/** يقرأ السوق/اللغة من مخزن LocaleContext الموحَّد. */
function readLocaleState(): { market?: string; locale?: string } {
  if (typeof window === "undefined") return {};
  try {
    const raw = localStorage.getItem("lux-locale-v3");
    if (!raw) return {};
    const parsed = JSON.parse(raw) as { market?: unknown; locale?: unknown };
    return {
      market: typeof parsed.market === "string" ? parsed.market : undefined,
      locale: typeof parsed.locale === "string" ? parsed.locale : undefined,
    };
  } catch {
    return {};
  }
}

export function useCartSync() {
  const syncCart = useCartStore((s) => s.syncCart);
  const items = useCartStore((s) => s.items);
  const checkoutUrl = useCartStore((s) => s.checkoutUrl);
  const { user } = useAuth();
  const lastSent = useRef<string>("");
  const eligibility = useSyncExternalStore(recoveryEligibilityState.subscribe,
    recoveryEligibilityState.getSnapshot, recoveryEligibilityState.getSnapshot);
  useEffect(() => {
    if (!CART_RECOVERY_SOURCE_ENABLED || !user) return;
    const ticket = recoveryEligibilityState.beginRead(user.id);
    if (!ticket) return;
    const locale = readLocaleState().locale === "ar" ? "ar" : "en";
    void getCartRecoveryEligibility({ data: { locale } }).then((result) => {
      recoveryEligibilityState.finishRead(ticket, result);
    }).catch(() => recoveryEligibilityState.fail(ticket));
    // A lease is never activity: no snapshot enrollment or replay when this resolves.
  }, [user?.id, eligibility.known, eligibility.reading]);


  useEffect(() => {
    if (!CART_RECOVERY_SOURCE_ENABLED || !user) return;
    const receipts = createRecoveryReceiptQueue({
      owner: user.id, currentOwner: () => getAuthSnapshot().userId,
      currentRevision: () => getAuthSnapshot().identityRevision,
      record: async ({ initiatingIdentityRevision: _revision, terminalRevision, ...event }) => {
        const ticket = event.kind === "empty" && event.initiatingUserId && terminalRevision !== undefined
          ? { owner: event.initiatingUserId, revision: terminalRevision } : null;
        const result = await recordCartRecoveryMutation({ data: event });
        if (result.ok && event.kind === "empty") recoveryEligibilityState.afterAcknowledgment(ticket);
        return result;
      },
      quarantine: (input) => quarantineCartRecovery({ data: input }),
    });
    const unsubscribe = onRecoveryMutation((event) => {
      void receipts.accept(event);
      const identity = getAuthSnapshot();
      if (event.kind === "mutation" && event.initiatingUserId === identity.userId &&
        event.initiatingIdentityRevision === identity.identityRevision)
        recoveryEligibilityState.retryAfterActivity(event.initiatingUserId);
    });
    return () => { receipts.dispose(); unsubscribe(); };
  }, [user?.id]);

  useEffect(() => {
    // لا توجد سلّة Shopify لمزامنتها: تجنّب أي عمل عند فتح الصفحة أو العودة للتبويب.
    if (!checkoutUrl && items.length === 0) return;
    syncCart();
    const onVis = () => {
      if (document.visibilityState === "visible") syncCart();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [syncCart, checkoutUrl, items.length]);

  // لقطة السلّة (مسجَّل → user_id · مجهول → visitor_id) تظهر للأدمن
  // وتُغذّي حملة «سلّة بلا شراء» للمجهولين.
  useEffect(() => {

    // لا ننشئ لقطة فارغة لزائر جديد. بعد وجود سلّة، يبقى إرسال الحالة الفارغة
    // مسؤولية clearCart/تدفّق الطلب، لا الصفحة الرئيسية.
    if (!checkoutUrl && items.length === 0) return;

    const payloadItems = items.slice(0, 50).map((i) => ({
      handle: i.product?.node?.handle ?? null,
      title: i.sampleDisplayName ?? i.product?.node?.title ?? null,
      variantTitle: i.variantTitle ?? null,
      image: i.sampleImageUrl ?? i.product?.node?.images?.edges?.[0]?.node?.url ?? null,
      price: Number(i.price?.amount ?? 0) || 0,
      quantity: i.quantity,
      isGiftSample: !!i.isSampleGift,
      variantId: i.variantId ?? null,
      selectedOptions: (i.selectedOptions ?? []).slice(0, 10).map((o) => ({
        name: String(o.name ?? "").slice(0, 120),
        value: String(o.value ?? "").slice(0, 200),
      })),
    }));


    const itemsCount = items.reduce((s, i) => s + i.quantity, 0);
    const cartValue = items.reduce((s, i) => s + Number(i.price?.amount ?? 0) * i.quantity, 0);
    const { market, locale } = readLocaleState();

    const payload = {
      items: payloadItems,
      itemsCount,
      cartValue: Math.round(cartValue * 100) / 100,
      cartCurrency: items[0]?.price?.currencyCode ?? null,
      market: market ?? null,
      locale: locale ?? null,
      checkoutUrl: checkoutUrl ?? null,
    };

    const key = JSON.stringify(payload);
    if (key === lastSent.current) return;

    const timer = setTimeout(() => {
      lastSent.current = key;
      if (user) {
        void syncCartSnapshot({ data: payload }).catch((err) => {
          console.error("[useCartSync] snapshot failed:", err);
        });
        return;
      }
      const visitorId = getVisitorId();
      if (!visitorId) return;
      void syncAnonCartSnapshot({ data: { ...payload, visitorId } }).catch((err) => {
        console.error("[useCartSync] anon snapshot failed:", err);
      });
    }, 1200);

    return () => clearTimeout(timer);
  }, [user, items, checkoutUrl]);
}
